import { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { get, post } from "src/utils/networkUtils";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import useWithUrlTenant from "src/hooks/useTenant";

export default function AddMatchPage() {
  const navigate = useNavigate();
  const withTenant = useWithUrlTenant();
  
  // Use tenant competitions hook - it handles tenant ID from URL params
  const { tenantCompetitions, loading: competitionsLoading, tenantId } = useTenantCompetitions();
  
  const [competitionId, setCompetitionId] = useState("");
  const [teams, setTeams] = useState([]);
  const [homeTeam, setHomeTeam] = useState("");
  const [awayTeam, setAwayTeam] = useState("");
  const [date, setDate] = useState(new Date());
  const [matchDay, setMatchDay] = useState("");
  const [period, setPeriod] = useState("");
  const [externalId, setExternalId] = useState("");
  const [status, setStatus] = useState("");
  
  const previousTenantCompetitionsRef = useRef([]);

  // Reset and auto-select the first competition when tenant competitions change
  useEffect(() => {
    const competitionsString = JSON.stringify(tenantCompetitions);
    const previousCompetitionsString = JSON.stringify(previousTenantCompetitionsRef.current);
    
    // Only update if competitions actually changed
    if (competitionsString !== previousCompetitionsString) {
      previousTenantCompetitionsRef.current = tenantCompetitions;
      
      if (tenantCompetitions.length > 0) {
        // Reset team selections when tenant changes
        setCompetitionId(tenantCompetitions[0].competitionId);
        setHomeTeam("");
        setAwayTeam("");
      } else {
        setCompetitionId("");
      }
    }
  }, [tenantCompetitions]);

  useEffect(() => {
    if (competitionId) {
      get(`/admin/teams?competition_id=${competitionId}`)
        .then((res) => {
          setTeams(res.teams || []);
        })
        .catch((err) => {
          console.error("Failed to load teams:", err);
          setTeams([]);
        });
    } else {
      setTeams([]);
      setHomeTeam("");
      setAwayTeam("");
    }
  }, [competitionId]);

  const handleSubmit = async () => {
    if (homeTeam === awayTeam) {
      return;
    }

    const payload = {
      competitionId,
      homeTeamId: homeTeam,
      awayTeamId: awayTeam,
      startDateUtc: date.toISOString(),
      matchDay,
      period,
      externalId,
      status,
      ...(tenantId && { tenantId: Number(tenantId) }), // Add tenant ID to payload if present
    };
    try {
      await post("/fixtures", payload);
      navigate(withTenant("/dashboard/fixtures"));
    } catch (error) {
      console.error("Error creating fixture:", error);
      navigate(withTenant("/dashboard/fixtures"));
    }
  };

  const handleCancel = () => {
    navigate(withTenant("/dashboard/fixtures"));
  };

  return (
    <Container>
      <Button variant="outlined" onClick={handleCancel} sx={{ mb: 6 }}>
        Cancel
      </Button>
      <Typography variant="h4" mb={4}>
        Add Match
      </Typography>
      <Stack spacing={3} sx={{ maxWidth: 400 }}>
        <FormControl fullWidth>
          <InputLabel>Competition</InputLabel>
          <Select
            value={competitionId}
            onChange={(e) => setCompetitionId(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            disabled={competitionsLoading}
          >
            {tenantCompetitions.map((tc) => (
              <MenuItem key={tc.competitionId} value={tc.competitionId}>
                {tc.competitionName || tc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!competitionId}>
          <InputLabel>Home Team</InputLabel>
          <Select
            value={homeTeam}
            onChange={(e) => setHomeTeam(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
          >
            {teams.map((t) => (
              <MenuItem key={t.scoreableId} value={t.scoreableId}>
                {t.knownname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!competitionId}>
          <InputLabel>Away Team</InputLabel>
          <Select
            value={awayTeam}
            onChange={(e) => setAwayTeam(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
          >
            {teams.map((t) => (
              <MenuItem key={t.scoreableId} value={t.scoreableId}>
                {t.knownname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Match Date & Time"
            value={date}
            onChange={setDate}
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>

        <TextField
          label="Match Day"
          type="number"
          value={matchDay}
          onChange={(e) => setMatchDay(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"></InputAdornment>,
          }}
        />

        <FormControl fullWidth>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="PreMatch">PreMatch</MenuItem>
            <MenuItem value="PostMatch">FullTime</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="External ID"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
        />

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="UPCOMING">UPCOMING</MenuItem>
            <MenuItem value="IN_PROGRESS">LIVE</MenuItem>
            <MenuItem value="COMPLETED">FINISHED</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!competitionId || !homeTeam || !awayTeam}
        >
          Add Match
        </Button>
      </Stack>
    </Container>
  );
}
